import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { XdsTab, XdsTabsComponent } from 'src/app/shared/ui/tabs/tabs.component';
import { XdsViewportService } from 'src/app/shared/ui/a11y/viewport.service';
import { TaskAllowedAction } from '../models';
import {
  buildTasksTabs,
  canViewTeamReportTab,
  resolveActiveTasksTab,
  tasksTabRoute,
  TasksNavCounts,
} from './tasks-nav.logic';
import { SubscriptionService } from '../../feature-toggle/services/subscription.service';
import { Feature } from '../../feature-toggle/enums/features.enum';
import { TaskSettingsService } from '../settings/task-settings.service';
import { TaskSettings, projectExtensionFlags } from '../settings/task-settings.model';

/**
 * `app-tasks-tabs` — die In-Page-Reiter des Aufgaben-Bereichs (`xds-tabs`,
 * 06-ENTRYPOINT-NAV.md §2). Rendert „Übersicht" (immer), „Routinen" (E3),
 * „Lage" (bei `ViewOperations` ∈ `AllowedActions`; älterer Server: am selben Platz
 * „Compliance" bei `ViewCompliance`, Ziel `/tasks/lage/nachweise`), „Projekte" (E4).
 *
 * Seit der Plan-Freigabe (08/2026) kommt der **Tarif** als dritte Bedingung hinzu:
 * „Routinen" verlangt `feature.tasks_routines` (ab Plus), „Compliance"
 * `feature.tasks_compliance` (nur Pro). Der Stand wird hier aus dem
 * {@link SubscriptionService} gelesen und an `buildTasksTabs` weitergegeben; ist er
 * unbekannt, bleiben die Reiter sichtbar (fail-open).
 *
 * Seit den Aufgaben-Einstellungen (09/2026) blenden zusätzlich die Firmen-Schalter
 * `routinesEnabled`/`complianceEnabled` die Reiter aus ({@link TaskSettingsService}). Bis die
 * Einstellungen da sind, gelten deren Defaults (= an) — also ebenfalls fail-open.
 *
 * Kontrolliert: `activeId` = aktueller Tab; bei Wechsel wird auf die passende
 * Task-Route navigiert (`overview`/`routines`/`compliance`) und `(tabChange)`
 * emittiert. Nicht-sichtbare Reiter werden gar nicht gerendert (kein disabled).
 *
 * ⚠️ INTEGRATIONS-HANDOFF (A2/A4/A6): Diese Leiste oben in die jeweilige
 * Page-Komponente einsetzen, z. B.
 *   `<app-tasks-tabs activeId="overview" [allowedActions]="scopeAllowedActions"></app-tasks-tabs>`
 * (`allowedActions` = die `AllowedActions` des Übersichts-/Scope-DTOs; steuert
 * die Compliance-Sichtbarkeit server-autoritativ).
 */
@Component({
  selector: 'app-tasks-tabs',
  standalone: true,
  imports: [CommonModule, XdsTabsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <xds-tabs
      [tabs]="tabs"
      [activeId]="resolvedActiveId"
      [dense]="dense"
      (activeIdChange)="onTabChange($event)"
    ></xds-tabs>
  `,
})
export class TasksTabsComponent implements OnDestroy {
  /**
   * Aktiver Reiter (`overview` | `routines` | `lage` | `compliance` | `throughput` | `team` |
   * `projects`). `lage` und `compliance` bezeichnen denselben Platz ({@link resolvedActiveId}).
   */
  @Input() activeId = 'overview';
  /** E3-Rollout: „Routinen" einblenden. */
  @Input() routinesEnabled = false;
  /** E4-Rollout: „Projekte" einblenden (nicht implementiert). */
  @Input() projectsEnabled = false;
  /** Server-`AllowedActions` des Scopes — steuert die Compliance-Sichtbarkeit. */
  @Input() allowedActions: readonly (TaskAllowedAction | string)[] | null = null;
  /** Optionale Pro-Reiter-Zähler. */
  @Input() badges: Record<string, TasksNavCounts | undefined> | null = null;
  /** Automatische Router-Navigation bei Tab-Wechsel unterbinden (nur `(tabChange)`). */
  @Input() navigate = true;

  /** Emittiert die neue Tab-Id bei Wechsel. */
  @Output() tabChange = new EventEmitter<string>();

  /**
   * `subscriptionService`, `viewport`, `taskSettings` und `cdr` sind **optional und die
   * letzten Parameter**: einige Specs bauen diese Komponente positionell per
   * `new TasksTabsComponent(router)`. Ein Pflichtparameter (oder ein `inject()` im
   * Feldinitialisierer) würde sie mit NG0203 brechen.
   */
  constructor(
    private readonly router: Router,
    private readonly subscriptionService?: SubscriptionService,
    private readonly viewport?: XdsViewportService,
    @Optional() taskSettings?: TaskSettingsService,
    @Optional() cdr?: ChangeDetectorRef,
  ) {
    // OnPush: Die Einstellungen kommen asynchron und sind kein Input — ohne markForCheck
    // bliebe ein abgeschalteter Reiter bis zum nächsten Ereignis stehen.
    this.settingsSub = taskSettings?.settings$.subscribe((settings) => {
      this.settings = settings;
      cdr?.markForCheck();
    });
  }

  /** Firmenweite Aufgaben-Einstellungen; `null` = unbekannt → Reiter bleiben sichtbar. */
  private settings: TaskSettings | null = null;
  private readonly settingsSub?: Subscription;

  ngOnDestroy(): void {
    this.settingsSub?.unsubscribe();
  }

  /**
   * Kompakte Reiterhöhe (T-31) — auf dem Telefon 64px → 44px.
   *
   * ⚠️ **Bewusst KEIN `@Input()` mehr** (geändert am 06.09.2026). Als durchgereichter
   * Input hing die Höhe daran, ob die einzelne Seite ihn setzt — und genau eine von vier
   * tat das. Gemessen auf 390×844×3 war dieselbe Leiste auf „Übersicht" **39,4px** hoch
   * (dicht: Polster 8/12, `display:flex`) und auf „Routinen"/„Compliance" **47,4px**
   * (Polster 12/16, `display:block`). Beim Reiterwechsel sprang deshalb alles darunter
   * um 8px — in der Abnahme gemeldet als „die Tabs ruckeln hin und her".
   *
   * Die Dichte ist eine Eigenschaft der **Fläche**, nicht der Seite. Diese Leiste ist der
   * gemeinsame Baustein aller Aufgaben-Reiter; sie entscheidet sie deshalb selbst, und
   * keine Aufrufstelle kann davon abweichen.
   *
   * Ohne `viewport` (positionell gebaute Specs) bleibt es bei `false` — dieselbe
   * Vorgabe, die der Service selbst ohne `matchMedia` liefert.
   */
  get dense(): boolean {
    return this.viewport?.deviceClass() === 'phone';
  }

  /**
   * `activeId` abgeglichen mit den sichtbaren Reitern: „Lage" und der Rückfall „Compliance" sind
   * derselbe Platz. Die Nachweis-Seite (`/tasks/lage/nachweise`) meldet `compliance` und hebt
   * damit — je nach Server — „Lage" oder „Compliance" hervor.
   */
  get resolvedActiveId(): string {
    return resolveActiveTasksTab(this.activeId, this.tabs);
  }

  get tabs(): XdsTab[] {
    return buildTasksTabs({
      routinesEnabled: this.routinesEnabled,
      projectsEnabled: this.projectsEnabled,
      allowedActions: this.allowedActions,
      badges: this.badges ?? undefined,
      routinesInPlan: this.tierInPlan(Feature.TASK_ROUTINES),
      complianceInPlan: this.tierInPlan(Feature.TASK_COMPLIANCE),
      routinesSettingEnabled: this.settings?.routinesEnabled,
      complianceSettingEnabled: this.settings?.complianceEnabled,
      // AP-45: „Durchlauf" nur mit „Aufträge nutzen" UND „Durchlauf-Auswertung" (BE-6);
      // unbekannte Einstellungen = aus (E-05).
      throughputEnabled: this.settings ? projectExtensionFlags(this.settings).throughputViewEnabled : false,
      // „Auswertung" (09.10.2026): Schalter UND Server-Aktion; unbekannt = aus.
      teamReportEnabled: canViewTeamReportTab(
        this.settings?.moduleEnabled !== false && this.settings?.personReportEnabled,
        this.allowedActions,
      ),
    });
  }

  /**
   * Tarif-Stufe gebucht? `undefined` = unbekannt → der Reiter bleibt sichtbar.
   * Ohne den `featuresLoaded`-Vorbehalt würde der Reiter beim ersten Paint (oder wenn der
   * Abo-Abruf scheitert) verschwinden, obwohl der Tarif ihn enthält.
   */
  private tierInPlan(feature: Feature): boolean | undefined {
    if (!this.subscriptionService?.featuresLoaded) return undefined;
    return this.subscriptionService.hasFeature(feature);
  }

  onTabChange(id: string): void {
    this.activeId = id;
    this.tabChange.emit(id);
    if (this.navigate) {
      // 'projects' (E4) ist nicht gebaut → keine Route; bleibt auf aktivem Tab.
      if (id === 'projects') return;
      // `lage` → /tasks/lage, Rückfall `compliance` → /tasks/lage/nachweise (L1).
      this.router.navigate(tasksTabRoute(id));
    }
  }
}
